import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, Observable, switchMap, tap } from 'rxjs';
import { SoftBanSelectors } from 'src/app/core/state/selectors/soft-ban.selectors';
import { SoftBanActions } from 'src/app/core/state/slices/soft-ban/soft-ban.actions';

/**
 * Facade is a simpler public interface that is provided to abstract the store
 * from components and services.
 *
 * Facade simplifies accessing and modifying NgRx state by masking internal
 * all interactions with the Store, actions, reducers, selectors, and Effects.
 */
@Injectable({
  providedIn: 'root',
})
export class SoftBanFacade {
  constructor(private store: Store) {}

  // Soft Ban status observable to be used instead of the one in soft-ban.service.ts.
  get isSoftBanActive$(): Observable<boolean> {
    return this.store.select(SoftBanSelectors.selectLoaded).pipe(
      // For the consistency of the guard behaviour it is required to
      // provide soft ban status only once it has been initialised.
      filter((isLoaded) => isLoaded),
      switchMap(() => this.store.select(SoftBanSelectors.selectHasActiveSoftBan)),
    );
  }

  /**
   * Determines if modal can be shown on a given route if hasn't been presented once already.
   *
   * @param route - expects current route as string.
   */
  canShowModal$(route: string): Observable<boolean> {
    return this.store.select(SoftBanSelectors.selectCanShowModal(route));
  }

  /**
   * Adds a route where modal has been presented to the array of routes in state.
   *
   * @param trigger - for tracking what triggered status change.
   * @param route - expects current route as string.
   */
  addModalShownRoute(trigger: string, route: string): void {
    this.store.dispatch(SoftBanActions.addModalShownRoute({ trigger, route }));
  }

  /**
   * Fot setting status of the soft ban.
   *
   * @param trigger - for tracking what triggered status change.
   * @param status - setting soft ban to enabled or disabled.
   */
  setSoftBanStatus(trigger: string, status: boolean): void {
    this.store.dispatch(SoftBanActions.setStatus({ trigger, status }));
  }

  /**
   * Initialises Soft Ban Feature state.
   *
   * @param trigger - for tracking what triggered status change.
   */
  initSoftBanStatus(trigger: string): void {
    firstValueFrom(
      this.store.select(SoftBanSelectors.selectCanInitialise).pipe(
        tap((canInitialise) => {
          if (canInitialise) {
            this.store.dispatch(SoftBanActions.getStatus({ trigger }));
          }
        }),
      ),
    );
  }

  /**
   * For resetting status of the soft ban.
   *
   * @param trigger - for tracking what triggered status change.
   */
  resetSoftBanStatus(trigger: string): void {
    this.store.dispatch(SoftBanActions.resetState({ trigger }));
  }
}
